<template>
  <div
    class="progress-linear"
    :class="computedClass"
    :style="computedStyle"
  >
    <div class="progress-linear__background" />
    <div class="progress-linear__buffer" />
    <div
      class="progress-linear__determinate"
      :style="fillStyle"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  progress?: number
  height?: string
  variant?: 'secondary' | 'light'
  animated?: boolean
  indeterminate?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

const props = withDefaults(defineProps<Props>(), {
  progress: 0,
  height: '4px',
  variant: 'secondary',
  animated: true,
  indeterminate: false,
  size: 'md',
})

const computedClass = computed(() => ({
  'progress-linear--indeterminate': props.indeterminate,
  'progress-linear--animated': props.animated && !props.indeterminate,
  [`progress-linear--${props.size}`]: true,
  [`progress-linear--${props.variant}`]: true,
}))

const computedStyle = computed(() => ({
  '--progress-linear-height': props.height,
}))

const fillStyle = computed(() => {
  if (props.indeterminate) {
    return {}
  }

  return {
    width: `${Math.min(100, Math.max(0, props.progress))}%`,
  }
})
</script>

<style lang="scss" scoped>
.progress-linear {
  width: 100%;
  height: var(--progress-linear-height);
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  cursor: pointer;

  &__background {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    position: absolute;
    top: 0;
    left: 0;
  }

  &__buffer {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    position: absolute;
    top: 0;
    left: 0;
  }

  &__determinate {
    height: 100%;
    background-color: var(--color-surface-primary);
    border-radius: inherit;
    position: absolute;
    top: 0;
    left: 0;
    transition: width 0.3s ease;
  }

  &--xs {
    --progress-linear-height: 2px;
  }

  &--sm {
    --progress-linear-height: 4px;
  }

  &--md {
    --progress-linear-height: 6px;
  }

  &--lg {
    --progress-linear-height: 8px;
  }

  &--xl {
    --progress-linear-height: 10px;
  }

  &--secondary {
    .progress-linear__background {
      background-color: var(--color-surface-placeholder);
    }

    .progress-linear__buffer {
      background-color: var(--color-surface-placeholder);
    }
  }

  &--light {
    .progress-linear__background {
      background-color: var(--color-surface-bg);
    }

    .progress-linear__buffer {
      background-color: var(--color-surface-bg);
    }
  }

  &--animated &__determinate {
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &--indeterminate {
    .progress-linear__determinate {
      width: 30% !important;
      animation: progress-linear-indeterminate 1.5s infinite linear;
    }
  }
}

@keyframes progress-linear-indeterminate {
  0% {
    transform: translateX(-100%);
  }

  50% {
    transform: translateX(0%);
  }

  100% {
    transform: translateX(100%);
  }
}
</style>
